import {
  MeshBuilder,
  StandardMaterial,
  Color3,
  type Scene,
  type AbstractMesh,
  type ShadowGenerator
} from '@babylonjs/core';

export class BossMesh {
  public rootMesh: AbstractMesh;
  public bodyMesh: AbstractMesh;
  public telegraphMesh: AbstractMesh;
  private crystalMat: StandardMaterial;
  private animTimer: number = 0;

  constructor(scene: Scene, shadowGenerator?: ShadowGenerator) {
    this.rootMesh = MeshBuilder.CreateBox('boss_root', { size: 0.1 }, scene);
    this.rootMesh.isVisible = false;

    // Torso / Rock body
    this.bodyMesh = MeshBuilder.CreateBox(
      'boss_body',
      { width: 2.2, height: 3.2, depth: 1.8 },
      scene
    );
    this.bodyMesh.parent = this.rootMesh;
    this.bodyMesh.position.y = 1.6;

    const rockMat = new StandardMaterial('boss_rock_mat', scene);
    rockMat.diffuseColor = new Color3(0.18, 0.2, 0.26);
    rockMat.specularColor = new Color3(0.3, 0.3, 0.4);
    this.bodyMesh.material = rockMat;

    // Crystal Spikes
    this.crystalMat = new StandardMaterial('boss_crystal_mat', scene);
    this.crystalMat.diffuseColor = new Color3(0.65, 0.25, 0.95);
    this.crystalMat.emissiveColor = new Color3(0.4, 0.1, 0.6);

    const spike1 = MeshBuilder.CreateCylinder(
      'boss_spike_1',
      { height: 1.8, diameterTop: 0.1, diameterBottom: 0.6 },
      scene
    );
    spike1.parent = this.bodyMesh;
    spike1.position.set(-0.8, 1.4, -0.4);
    spike1.rotation.z = -0.3;
    spike1.material = this.crystalMat;

    const spike2 = MeshBuilder.CreateCylinder(
      'boss_spike_2',
      { height: 1.8, diameterTop: 0.1, diameterBottom: 0.6 },
      scene
    );
    spike2.parent = this.bodyMesh;
    spike2.position.set(0.8, 1.4, -0.4);
    spike2.rotation.z = 0.3;
    spike2.material = this.crystalMat;

    // Telegraph Ground Warning Ring
    this.telegraphMesh = MeshBuilder.CreateTorus(
      'boss_telegraph',
      { diameter: 6.0, thickness: 0.3, tessellation: 24 },
      scene
    );
    this.telegraphMesh.parent = this.rootMesh;
    this.telegraphMesh.position.y = 0.1;

    const telegraphMat = new StandardMaterial('telegraph_mat', scene);
    telegraphMat.diffuseColor = new Color3(0.9, 0.1, 0.1);
    telegraphMat.emissiveColor = new Color3(0.8, 0.1, 0.1);
    this.telegraphMesh.material = telegraphMat;
    this.telegraphMesh.isVisible = false;

    if (shadowGenerator) {
      shadowGenerator.addShadowCaster(this.bodyMesh);
      shadowGenerator.addShadowCaster(spike1);
      shadowGenerator.addShadowCaster(spike2);
    }
  }

  public setPosition(x: number, y: number, z: number): void {
    this.rootMesh.position.set(x, y, z);
  }

  public update(dt: number, isTelegraphing: boolean, isEnraged: boolean, isDefeated: boolean): void {
    if (isDefeated) {
      this.telegraphMesh.isVisible = false;
      this.rootMesh.position.y = Math.max(-2.0, this.rootMesh.position.y - dt * 2.0);
      return;
    }

    this.animTimer += dt * (isEnraged ? 4.0 : 2.0);
    this.bodyMesh.position.y = 1.6 + Math.sin(this.animTimer) * 0.1;

    // Enrage visual glow
    if (isEnraged) {
      this.crystalMat.diffuseColor.set(0.95, 0.15, 0.25);
      this.crystalMat.emissiveColor.set(0.7, 0.1, 0.15);
    }

    // Telegraph pulsing
    this.telegraphMesh.isVisible = isTelegraphing;
    if (isTelegraphing) {
      const pulse = 1.0 + Math.sin(this.animTimer * 12.0) * 0.08;
      this.telegraphMesh.scaling.set(pulse, 1, pulse);
    }
  }
}
